import CreationTool from 'creation/src/creation-tools/types/creation-tool'
import CreationToolOutputType from 'creation/src/creation-tools/types/output-type'

/**
 * Tailwind replacements for the legacy CreationToolGradient colour pairs.
 * Values mirror app/static/js/site/creation-tools/types/gradient.ts.
 */
export const gradientClassNames = {
    indigoPurple: 'from-giphyIndigo to-giphyPurple',
    indigoBlue: 'from-giphyIndigo to-giphyBlue',
    pinkIndigo: 'from-giphyPink to-giphyIndigo',
    blueGreen: 'from-giphyBlue to-giphyGreen',
    blackBlack: 'from-giphyBlack to-black',
} as const

export const toolGradientClassName = (tool: CreationTool): string => {
    switch (tool) {
        case CreationTool.SuperCut:
            return gradientClassNames.blackBlack
        case CreationTool.GifMaker:
            return gradientClassNames.pinkIndigo
        case CreationTool.Upload:
            return gradientClassNames.indigoBlue
    }
}

export const outputTypeGradientClassName = (type: CreationToolOutputType): string => {
    switch (type) {
        case CreationToolOutputType.Sticker:
            return gradientClassNames.blueGreen
        default:
            return gradientClassNames.indigoPurple
    }
}

/**
 * Legacy's exact gradient formula (`GradientBlock`, components/blocks.ts):
 * `linear-gradient(45deg, C0 0%, C1 33%, C0 66%, C1 100%)` — a 4-stop,
 * back-and-forth gradient with no explicit interpolation colour space, so it
 * renders in the browser's default (sRGB). `gradientClassNames`/
 * `bg-linear-45` above only reproduce a 2-stop `from`/`to` wash, and Tailwind
 * v4's `bg-linear-*` utilities compose `in oklab` by default — close enough
 * for the *un-measured* washes `container.tsx`/`drop-zone.tsx` use (see
 * drop-zone.tsx's own comment: "not a measured value"), but not for
 * continue-button.tsx, which is a real, measured element
 * (task-13-report.md "Fix round 2"). Tailwind can't express 4 custom-
 * position, non-oklab stops through `from`/`via`/`to`, so this is a literal
 * arbitrary `background-image` value instead — literal arbitrary values are
 * this codebase's own idiom for exactly this kind of case (e.g.
 * `text-[24px]`, `w-[1040px]` throughout `/create`). CSS custom properties
 * (`--color-giphy*`, app/global.css `@theme`), not hex literals, so this
 * tracks the palette if it ever changes.
 *
 * Only `indigoPurple` is needed — `ContinueButton`'s only caller
 * (video-trim-buttons.tsx) never passes a different gradient, matching
 * legacy's `ContinueButton`'s own default (`CreationToolGradient.IndigoPurple`,
 * components/continue-button.tsx).
 *
 * Written as a full literal, not built from an interpolated template
 * string: Tailwind's build-time scanner extracts candidate class names from
 * source *text*, not runtime values — a template literal with `${from}`/
 * `${to}` placeholders never appears in source as the final class name, so
 * Tailwind never generates the rule for it. Confirmed by measurement: the
 * interpolated version compiled, typechecked, and rendered the class in the
 * DOM, but `getComputedStyle(button).backgroundImage` was `none` — no CSS
 * rule existed for it at all.
 */
export const exactGradientClassNames = {
    indigoPurple:
        'bg-[linear-gradient(45deg,var(--color-giphyIndigo)_0%,var(--color-giphyPurple)_33%,var(--color-giphyIndigo)_66%,var(--color-giphyPurple)_100%)]',
} as const

/**
 * The rating field's per-rating gradients. Legacy's `CreationToolRating.gradient()`
 * returns a `types` package `Gradient` name per rating; these are the same colour
 * pairs (`gradientToHexColors` in `packages/utils`) expressed the way this app's
 * own gradients are — `to right`, two stops — because the rating control uses
 * `@/components/toggle` rather than `ui`'s SegmentedControl.
 *
 * Written as complete literal class names for the reason the block above
 * explains: Tailwind's scanner reads source text, so an interpolated class name
 * never gets a rule generated.
 */
export const ratingGradientClassNames = {
    // indigo-blue
    g: 'bg-[linear-gradient(to_right,var(--color-giphyIndigo),var(--color-giphyBlue))]',
    // indigo-purple
    pg: 'bg-[linear-gradient(to_right,var(--color-giphyIndigo),var(--color-giphyPurple))]',
    // red-yellow
    pg13: 'bg-[linear-gradient(to_right,var(--color-giphyRed),var(--color-giphyYellow))]',
    // red-pink
    r: 'bg-[linear-gradient(to_right,var(--color-giphyRed),var(--color-giphyPink))]',
} as const
